import { Link, useRouterState } from '@tanstack/react-router';
import { ChevronRight, Plus } from 'lucide-react';
import type { Feature } from '@outss/ledger-core';
import {
  BUSINESS_NAVIGATION,
  COMING_SOON,
  NAVIGATION,
  NEW_MENU,
  isAvailable,
} from '@/app/navigation';
import { YedoMark, Logo } from '@/components/brand/logo';
import { BusinessNewMenu } from '@/components/portal/business-new-menu';
import { useEInvoicingEnabled } from '@/lib/einvoicing';
import { useTrackingSettings } from '@/lib/tracking';
import { useOrgLocale } from '@/lib/org-locale';
import { openNewDrawer } from '@/lib/new-drawer';
import { featureShown, useBusinessView, useFeatureSet } from '@/lib/portal';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarRail,
} from '@/components/ui/sidebar';

/** "+ New" button and four-column menu, as in QBO (document 05). */
function NewMenu() {
  const { taxPack } = useOrgLocale();
  const features = useFeatureSet();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button className="w-full justify-start gap-2" size="lg">
          <Plus className="size-4" /> New
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="max-h-[80vh] w-[min(640px,calc(100vw-1.5rem))] overflow-y-auto p-4"
      >
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {NEW_MENU.map((col) => (
            <div key={col.heading}>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {col.heading}
              </div>
              <ul className="space-y-1">
                {col.items
                  .filter(
                    (item) =>
                      (!item.pack || item.pack === taxPack) &&
                      featureShown(features, item.features),
                  )
                  .map((item) => (
                    <li key={item.label} title={isAvailable(item.to) ? undefined : COMING_SOON}>
                      {!isAvailable(item.to) ? (
                        <DropdownMenuItem disabled className="px-1 py-1" data-unavailable>
                          {item.label}
                        </DropdownMenuItem>
                      ) : item.drawer ? (
                        <DropdownMenuItem
                          className="px-1 py-1"
                          onSelect={() => openNewDrawer({ kind: item.drawer! })}
                        >
                          {item.label}
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem asChild className="px-1 py-1">
                          <Link to={item.to}>{item.label}</Link>
                        </DropdownMenuItem>
                      )}
                    </li>
                  ))}
              </ul>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function AppSidebar({ isStaff = true }: { isStaff?: boolean }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const business = useBusinessView();
  const navigation = business.on ? BUSINESS_NAVIGATION : NAVIGATION;
  const flags = { einvoicing: useEInvoicingEnabled(), projects: useTrackingSettings().projects };
  const { taxPack } = useOrgLocale();
  const features = useFeatureSet();
  const shown = (item: {
    flag?: keyof typeof flags;
    pack?: string;
    features?: readonly Feature[];
  }) =>
    (!item.flag || flags[item.flag]) &&
    (!item.pack || item.pack === taxPack) &&
    featureShown(features, item.features);
  const matches = (to: string) =>
    to === '/'
      ? pathname === '/'
      : // Settings is one entry for every /settings page
        to.startsWith('/settings/')
        ? pathname.startsWith('/settings/')
        : pathname === to || pathname.startsWith(`${to}/`);
  // Within a group only the most specific item is active (Sales › Overview is "/sales").
  const isActive = (to: string, siblings?: Array<{ to: string }>) =>
    matches(to) &&
    !(siblings ?? []).some((o) => o.to !== to && o.to.startsWith(to) && matches(o.to));

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="gap-3 p-3">
        <div className="flex h-7 items-center px-1">
          <Logo className="group-data-[collapsible=icon]:hidden" />
          <YedoMark className="hidden group-data-[collapsible=icon]:block" />
        </div>
        <div className="group-data-[collapsible=icon]:hidden">
          {business.on ? <BusinessNewMenu /> : <NewMenu />}
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarMenu>
            {navigation
              .filter((g) => (isStaff || !g.staffOnly) && shown(g))
              .map((group) =>
                group.items ? (
                  <Collapsible
                    key={group.label}
                    asChild
                    defaultOpen={group.items.some((i) => isActive(i.to))}
                    className="group/collapsible"
                  >
                    <SidebarMenuItem>
                      <CollapsibleTrigger asChild>
                        <SidebarMenuButton
                          tooltip={group.label}
                          isActive={group.items.some((i) => isActive(i.to))}
                        >
                          <group.icon />
                          <span>{group.label}</span>
                          <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                        </SidebarMenuButton>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <SidebarMenuSub>
                          {group.items.filter(shown).map((item) => (
                            <SidebarMenuSubItem
                              key={`${group.label}-${item.to}`}
                              title={isAvailable(item.to) ? undefined : COMING_SOON}
                            >
                              {isAvailable(item.to) ? (
                                <SidebarMenuSubButton
                                  asChild
                                  isActive={isActive(item.to, group.items)}
                                >
                                  <Link to={item.to}>
                                    <span>{item.label}</span>
                                  </Link>
                                </SidebarMenuSubButton>
                              ) : (
                                <SidebarMenuSubButton asChild aria-disabled>
                                  <span data-unavailable>{item.label}</span>
                                </SidebarMenuSubButton>
                              )}
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      </CollapsibleContent>
                    </SidebarMenuItem>
                  </Collapsible>
                ) : (
                  <SidebarMenuItem key={group.label}>
                    <SidebarMenuButton asChild tooltip={group.label} isActive={isActive(group.to!)}>
                      <Link to={group.to!}>
                        <group.icon />
                        <span>{group.label}</span>
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ),
              )}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>
      <SidebarRail />
    </Sidebar>
  );
}
